import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiHeaders } from '../../utils/apiHelpers';
import { 
  RefreshCw, Upload, FileText, TrendingUp, Users, Package, Search, 
  Calendar, PieChart, ChevronDown, ChevronUp, Sparkles, X, Eye
} from 'lucide-react';
import TenantMobileLayout from './TenantMobileLayout';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Formatta numero in formato italiano (es. 49.035,00)
const formatCurrency = (value, decimals = 2) => {
  const num = Number(value) || 0;
  return num.toLocaleString('it-IT', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

const TenantMobileFatture = ({ onBack, tenantDb }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [view, setView] = useState('anni'); // anni, list, fornitori, prodotti, categorie, cerca
  const [fatture, setFatture] = useState([]);
  const [statisticheAnni, setStatisticheAnni] = useState([]);
  const [statisticheFornitori, setStatisticheFornitori] = useState([]);
  const [statisticheProdotti, setStatisticheProdotti] = useState([]);
  const [statisticheCategorie, setStatisticheCategorie] = useState([]);
  const [selectedAnno, setSelectedAnno] = useState(new Date().getFullYear());
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [expandedFornitori, setExpandedFornitori] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Ricerca prodotti
  const [searchProdotti, setSearchProdotti] = useState('');
  const [prodottiRicerca, setProdottiRicerca] = useState([]);
  const [searchingProdotti, setSearchingProdotti] = useState(false);
  const fileInputRef = useRef(null);

  const getHeaders = useCallback(() => {
    return getApiHeaders();
  }, []);

  // Load statistiche anni on mount
  useEffect(() => {
    loadStatisticheAnni();
  }, []);

  // Load data when view changes
  useEffect(() => {
    if (view === 'anni') {
      setLoading(false);
      return;
    }
    if (view === 'list') loadFattureAnno();
    else if (view === 'fornitori') loadStatisticheFornitori();
    else if (view === 'prodotti') loadStatisticheProdotti();
    else if (view === 'categorie') loadStatisticheCategorie();
  }, [view, selectedAnno]);

  const loadStatisticheAnni = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/anni`, { headers: getHeaders() });
      const data = response.data.anni || response.data || [];
      // Normalizza i dati per il componente
      const normalizedData = data.map(a => ({
        anno: a.anno,
        count: a.totale_fatture || a.count || 0,
        totale: a.totale_importo || a.totale || 0
      }));
      setStatisticheAnni(normalizedData);
      if (normalizedData.length > 0 && !normalizedData.find(a => a.anno === selectedAnno)) {
        setSelectedAnno(normalizedData[0].anno);
      }
    } catch (error) {
      // Fallback: carica tutte le fatture e calcola gli anni
      try {
        const response = await axios.get(`${API}/gestione/fatture?limit=1000`, { headers: getHeaders() });
        const fatture = response.data.fatture || response.data || [];
        const anniMap = {};
        fatture.forEach(f => {
          const anno = f.anno || (f.data ? parseInt(f.data.substring(0, 4)) : new Date().getFullYear());
          if (!anniMap[anno]) anniMap[anno] = { anno, count: 0, totale: 0 };
          anniMap[anno].count++;
          anniMap[anno].totale += f.importo || 0;
        });
        const anni = Object.values(anniMap).sort((a, b) => b.anno - a.anno);
        setStatisticheAnni(anni);
        if (anni.length > 0) setSelectedAnno(anni[0].anno);
      } catch (e) {
        console.error('Error loading anni:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFattureAnno = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture?anno=${selectedAnno}&limit=500`, { headers: getHeaders() });
      setFatture(response.data.fatture || response.data || []);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const loadStatisticheFornitori = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/fornitori?anno=${selectedAnno}`, { headers: getHeaders() });
      setStatisticheFornitori(response.data.fornitori || response.data || []);
    } catch (error) {
      // Fallback
      const response = await axios.get(`${API}/gestione/fatture?anno=${selectedAnno}&limit=500`, { headers: getHeaders() });
      const fatture = response.data.fatture || response.data || [];
      const fornitoriMap = {};
      fatture.forEach(f => {
        const key = f.fornitore || 'Sconosciuto';
        if (!fornitoriMap[key]) fornitoriMap[key] = { fornitore: key, count: 0, totale: 0 };
        fornitoriMap[key].count++;
        fornitoriMap[key].totale += f.importo || 0;
      });
      setStatisticheFornitori(Object.values(fornitoriMap).sort((a, b) => b.totale - a.totale));
    } finally {
      setLoading(false);
    }
  };

  const loadStatisticheProdotti = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/prodotti?anno=${selectedAnno}`, { headers: getHeaders() });
      setStatisticheProdotti(response.data.prodotti || response.data || []);
    } catch (error) {
      setStatisticheProdotti([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatisticheCategorie = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestione/fatture/statistiche/categorie?anno=${selectedAnno}`, { headers: getHeaders() });
      setStatisticheCategorie(response.data.categorie || response.data || []);
    } catch (error) {
      setStatisticheCategorie([]);
    } finally {
      setLoading(false);
    }
  };

  // Funzione di ricerca fatture
  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      // Cerca nelle fatture
      const response = await axios.get(`${API}/gestione/fatture?search=${encodeURIComponent(term)}&limit=50`, { headers: getHeaders() });
      const results = response.data.fatture || response.data || [];
      setSearchResults(results);
    } catch (error) {
      console.error('Errore ricerca:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Funzione di ricerca prodotti - cerca in TUTTI gli anni
  const handleSearchProdotti = async (term) => {
    setSearchProdotti(term);
    if (!term || term.length < 2) {
      setProdottiRicerca([]);
      return;
    }
    
    setSearchingProdotti(true);
    try {
      // Cerca in TUTTI gli anni (senza filtro anno)
      const response = await axios.get(`${API}/gestione/fatture/prodotti/cerca?search=${encodeURIComponent(term)}&limit=100`, { headers: getHeaders() });
      setProdottiRicerca(response.data.prodotti || []);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
      setProdottiRicerca([]);
    } finally {
      setSearchingProdotti(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const response = await axios.post(`${API}/gestione/fatture/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`✅ Caricate ${response.data.caricati || 0} fatture`);
      if (response.data.errori?.length > 0) {
        toast.warning(`⚠️ ${response.data.errori.length} errori`);
      }
      loadStatisticheAnni();
      if (view !== 'anni') {
        if (view === 'list') loadFattureAnno();
        else if (view === 'fornitori') loadStatisticheFornitori();
      }
    } catch (error) {
      
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const categorizzaFatture = async () => {
    setCategorizing(true);
    let totaleElaborate = 0;
    let continuaElaborazione = true;
    
    try {
      while (continuaElaborazione) {
        const response = await axios.post(`${API}/gestione/fatture/categorizza-tutte`, {}, { headers: getHeaders() });
        const { categorizzate, rimanenti, messaggio } = response.data;
        
        totaleElaborate += categorizzate || 0;
        
        if (rimanenti > 0) {
          toast.info(`⏳ Elaborate ${totaleElaborate} fatture... (${rimanenti} rimanenti)`);
        } else {
          continuaElaborazione = false;
          toast.success(`✅ ${messaggio || 'Categorizzazione completata!'}`);
        }
        
        if (rimanenti === 0 || categorizzate === 0) {
          continuaElaborazione = false;
        }
      }
    } catch (error) {
      toast.error('Errore categorizzazione');
    } finally {
      setCategorizing(false);
    }
  };

  const tabs = [
    { id: 'anni', label: 'Anni', icon: Calendar },
    { id: 'list', label: 'Lista', icon: FileText },
    { id: 'categorie', label: 'Cat.', icon: PieChart },
    { id: 'fornitori', label: 'Forn.', icon: Users },
    { id: 'prodotti', label: 'Prod.', icon: Package },
    { id: 'cerca', label: 'Cerca', icon: Search },
  ];

  const filteredFatture = fatture.filter(f => 
    !searchTerm || 
    f.fornitore?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.numero?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <TenantMobileLayout title="Fatture Elettroniche" onBack={onBack}>
      <div className="space-y-4">
        {/* Action Buttons */}
        <div className="flex gap-2">
          <label 
            className={`flex-1 py-3 rounded-xl border border-cyan-500/30 flex items-center justify-center gap-2 text-cyan-400 font-medium cursor-pointer active:scale-98 transition-all ${uploading ? 'opacity-50' : ''}`}
            style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
          >
            {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span className="text-sm">Carica</span>
            <input type="file" multiple accept=".xml,.zip,.p7m" onChange={handleUpload} className="hidden" disabled={uploading} ref={fileInputRef} />
          </label>
          <button
            onClick={categorizzaFatture}
            disabled={categorizing}
            className={`flex-1 py-3 rounded-xl border border-purple-500/30 flex items-center justify-center gap-2 text-purple-400 font-medium active:scale-98 transition-all ${categorizing ? 'opacity-50' : ''}`}
            style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(0,0,0,0.2) 100%)' }}
          >
            {categorizing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            <span className="text-sm">AI Cat.</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-black/30 border border-cyan-500/20">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                view === tab.id 
                  ? 'bg-cyan-500 text-black' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="h-3 w-3" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Year Selector (if not on anni view) */}
        {view !== 'anni' && statisticheAnni.length > 0 && (
          <select
            value={selectedAnno}
            onChange={(e) => setSelectedAnno(parseInt(e.target.value))}
            className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none"
          >
            {statisticheAnni.map(a => (
              <option key={a.anno} value={a.anno}>{a.anno} ({a.count} fatture)</option>
            ))}
          </select>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
          </div>
        ) : (
          <>
            {/* Anni View */}
            {view === 'anni' && (
              <div className="space-y-3">
                {statisticheAnni.length === 0 ? (
                  <div className="text-center py-12 rounded-2xl border border-cyan-500/20" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}>
                    <FileText className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Nessuna fattura importata</p>
                    <p className="text-gray-500 text-sm mt-2">Carica file XML, ZIP o P7M</p>
                  </div>
                ) : (
                  statisticheAnni.map(anno => (
                    <button
                      key={anno.anno}
                      onClick={() => { setSelectedAnno(anno.anno); setView('list'); }}
                      className="w-full rounded-2xl p-4 border border-cyan-500/20 flex items-center justify-between active:scale-98 transition-all"
                      style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                          <Calendar className="h-6 w-6 text-cyan-400" />
                        </div>
                        <div className="text-left">
                          <p className="text-xl font-bold text-white">{anno.anno}</p>
                          <p className="text-sm text-gray-400">{anno.count} fatture</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-cyan-400">€{formatCurrency(anno.totale, 2)}</p>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* List View */}
            {view === 'list' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Cerca fornitore o numero..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500"
                  />
                </div>
                
                {filteredFatture.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">Nessuna fattura trovata</p>
                ) : (
                  filteredFatture.slice(0, 50).map((fattura, idx) => (
                    <div
                      key={fattura.id || idx}
                      onClick={() => setSelectedFattura(fattura)}
                      className="rounded-2xl p-4 border border-cyan-500/20 active:scale-98 transition-all cursor-pointer"
                      style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{fattura.fornitore || 'N/D'}</h3>
                          <p className="text-xs text-cyan-400">N. {fattura.numero || '-'}</p>
                        </div>
                        <p className="text-lg font-bold text-cyan-400">€{formatCurrency(fattura.importo)}</p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-400">{fattura.data || '-'}</p>
                        {fattura.categoria && (
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400">{fattura.categoria}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {filteredFatture.length > 50 && (
                  <p className="text-center text-gray-500 text-sm">E altre {filteredFatture.length - 50} fatture...</p>
                )}
              </div>
            )}

            {/* Fornitori View */}
            {view === 'fornitori' && (
              <div className="space-y-3">
                {statisticheFornitori.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">Nessun fornitore</p>
                ) : (
                  statisticheFornitori.slice(0, 30).map((f, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl p-4 border border-cyan-500/20"
                      style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{f.fornitore || f.nome || 'N/D'}</h3>
                          <p className="text-sm text-gray-400">{f.count || f.numero_fatture || 0} fatture</p>
                        </div>
                        <p className="text-lg font-bold text-cyan-400">€{formatCurrency(f.totale, 2)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Prodotti View */}
            {view === 'prodotti' && (
              <div className="space-y-4">
                {/* Search Input Prodotti */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchProdotti}
                    onChange={(e) => handleSearchProdotti(e.target.value)}
                    placeholder="Cerca prodotto..."
                    className="w-full pl-12 pr-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                  />
                  {searchProdotti && (
                    <button
                      onClick={() => { setSearchProdotti(''); setProdottiRicerca([]); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  )}
                </div>

                {/* Risultati ricerca prodotti */}
                {searchingProdotti ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-cyan-500" />
                  </div>
                ) : searchProdotti.length >= 2 ? (
                  prodottiRicerca.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">Nessun prodotto trovato per "{searchProdotti}"</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-400">{prodottiRicerca.length} prodott{prodottiRicerca.length === 1 ? 'o' : 'i'} trovat{prodottiRicerca.length === 1 ? 'o' : 'i'}</p>
                      {prodottiRicerca.map((gruppo, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl overflow-hidden border border-cyan-500/20"
                          style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                        >
                          {/* Header del gruppo prodotto */}
                          <div className="p-4 border-b border-gray-700/50">
                            <h3 className="font-bold text-white text-base mb-2">{gruppo.nome_prodotto}</h3>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="text-green-400 font-bold">€{formatCurrency(gruppo.prezzo_min, 2)}/pz</span>
                                {gruppo.fornitore_min && (
                                  <span className="text-green-300 text-xs">({gruppo.fornitore_min.split(' ')[0]})</span>
                                )}
                              </div>
                              {gruppo.prezzo_max > gruppo.prezzo_min && (
                                <>
                                  <span className="text-gray-500">→</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-red-400">€{formatCurrency(gruppo.prezzo_max, 2)}/pz</span>
                                    {gruppo.fornitore_max && (
                                      <span className="text-red-300 text-xs">({gruppo.fornitore_max.split(' ')[0]})</span>
                                    )}
                                  </div>
                                </>
                              )}
                              {gruppo.num_fornitori > 1 && (
                                <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">
                                  {gruppo.num_fornitori} fornitori
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Lista occorrenze ordinate per prezzo */}
                          <div className="divide-y divide-gray-700/30">
                            {gruppo.occorrenze.map((p, pIdx) => (
                              <div key={pIdx} className={`p-3 ${pIdx === 0 ? 'bg-green-500/10' : ''}`}>
                                <div className="flex items-start justify-between mb-1">
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-300 truncate">{p.descrizione}</p>
                                    <p className="text-xs text-gray-500">
                                      {p.quantita_fattura} conf. × {p.pezzi_confezione} pz = {p.pezzi_totali} pz totali
                                    </p>
                                  </div>
                                  <div className="text-right ml-2">
                                    <p className={`font-bold ${pIdx === 0 ? 'text-green-400' : 'text-cyan-400'}`}>
                                      €{formatCurrency(p.prezzo_singolo_pezzo, 2)}/pz
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      €{formatCurrency(p.prezzo_confezione, 2)}/conf.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-xs mt-1">
                                  <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded truncate max-w-[140px]">
                                    {p.fornitore}
                                  </span>
                                  <span className="text-gray-500">{p.data_fattura}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  /* Vista statistiche aggregate (default) */
                  <div className="space-y-3">
                    {statisticheProdotti.length === 0 ? (
                      <p className="text-center text-gray-400 py-8">Nessun prodotto</p>
                    ) : (
                      statisticheProdotti.slice(0, 30).map((p, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl p-4 border border-cyan-500/20"
                          style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-white truncate">{p.prodotto || p.nome || p.descrizione || 'N/D'}</h3>
                              <p className="text-sm text-gray-400">{p.quantita_totale || p.quantita || 0} pz • {p.num_fatture || 0} fatture</p>
                            </div>
                            <p className="text-lg font-bold text-cyan-400">€{formatCurrency(p.importo_totale || p.totale || p.prezzo_totale)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Categorie View */}
            {view === 'categorie' && (
              <div className="space-y-3">
                {statisticheCategorie.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">Nessuna categoria. Usa "AI Cat." per categorizzare.</p>
                ) : (
                  statisticheCategorie.map((c, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl p-4 border border-purple-500/20"
                      style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(0,0,0,0.3) 100%)' }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{c.categoria || c.nome || 'N/D'}</h3>
                          <p className="text-sm text-gray-400">{c.count || c.numero_fatture || 0} fatture</p>
                        </div>
                        <p className="text-lg font-bold text-purple-400">€{formatCurrency(c.totale, 2)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Cerca View */}
            {view === 'cerca' && (
              <div className="space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Cerca fattura, fornitore, prodotto..."
                    className="w-full pl-12 pr-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      onClick={() => { setSearchTerm(''); setSearchResults([]); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  )}
                </div>

                {/* Search Results */}
                {searching ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-cyan-500" />
                  </div>
                ) : searchTerm.length < 2 ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Inserisci almeno 2 caratteri</p>
                    <p className="text-gray-500 text-sm mt-1">Cerca per fornitore, numero fattura o prodotto</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Nessun risultato per "{searchTerm}"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-400">{searchResults.length} risultat{searchResults.length === 1 ? 'o' : 'i'}</p>
                    {searchResults.map((fattura, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedFattura(fattura)}
                        className="w-full text-left rounded-2xl p-4 border border-cyan-500/20 active:scale-98 transition-all"
                        style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white truncate">{fattura.fornitore || 'N/D'}</h3>
                            <p className="text-sm text-gray-400">N. {fattura.numero || 'N/D'} • {fattura.data || 'N/D'}</p>
                          </div>
                          <p className="text-lg font-bold text-cyan-400">€{formatCurrency(fattura.importo)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Fattura Detail Modal */}
      {selectedFattura && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center">
          <div
            className="w-full max-w-md rounded-t-3xl border-t border-cyan-500/30 max-h-[85vh] overflow-y-auto"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-6 border-b border-cyan-500/20 flex items-center justify-between sticky top-0 bg-inherit">
              <h2 className="text-lg font-bold text-white">Dettaglio Fattura</h2>
              <button onClick={() => setSelectedFattura(null)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Fornitore</p>
                  <p className="text-white font-medium">{selectedFattura.fornitore || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">P.IVA</p>
                  <p className="text-white font-medium">{selectedFattura.partita_iva || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Numero</p>
                  <p className="text-white font-medium">{selectedFattura.numero || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data</p>
                  <p className="text-white font-medium">{selectedFattura.data || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Importo</p>
                  <p className="text-cyan-400 font-bold text-lg">€{formatCurrency(selectedFattura.importo)}</p>
                </div>
                {selectedFattura.categoria && (
                  <div>
                    <p className="text-xs text-gray-500">Categoria</p>
                    <p className="text-purple-400 font-medium">{selectedFattura.categoria}</p>
                  </div>
                )}
              </div>

              {/* Linee fattura */}
              {selectedFattura.linee && selectedFattura.linee.length > 0 && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Prodotti ({selectedFattura.linee.length})</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedFattura.linee.map((linea, idx) => (
                      <div key={idx} className="p-3 rounded-xl bg-black/30 border border-cyan-500/10">
                        <p className="text-white text-sm truncate">{linea.descrizione || 'Prodotto'}</p>
                        <div className="flex justify-between mt-1 text-xs text-gray-400">
                          <span>{linea.quantita || 1} x €{formatCurrency(linea.prezzo_unitario)}</span>
                          <span className="text-cyan-400">€{formatCurrency(linea.prezzo_totale)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </TenantMobileLayout>
  );
};

export default TenantMobileFatture;
